import { Permissions } from '../../../../models/enums/permissions';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { getDocumentHelper } from '../../../../actions/utility-actions';
import { DataGroupHeader } from '../../../../components/data-group/DataGroupHeader';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import * as Forms from '../../../../components/form-elements/_index';
import * as Pages from '../../../../components/page-components/_index';
import { Table } from '../../../../components/tables/Table';
import { useRouteMiss } from '../../../../hooks/use-route-miss';
import { useServiceCenter } from '../../../../hooks/use-service-center';
import { LocalTimeRead } from '../../../../models/utilities/local-time-read';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { formatCurrency } from '../../../../utils/currency-formatter';
import { toLocalDateString } from '../../../../utils/date-utils';
import { navigateToPage } from '../../../../utils/history';
import * as transportActions from '../../../../transportation/actions';
import { EditPickupSidebar } from '../components/EditPickupSidebar';
import { PickupActionEditInfo } from '../components/PickupActionEditInfo';
import { PickupStatus } from '../../../../transportation/models/enums/pickup-status';
import { LoadSheetDocument } from '../../../../transportation/models/pickups/load-sheet-document';
import { PickupRead } from '../../../../transportation/models/pickups/pickup-read';
import { OrderSummary } from '../../../../transportation/models/shared/order-summary';
import { ProductionStatusDot } from '../../../../transportation/components/shipments/ProductionStatusDot';
import { TransportationSvc } from '../../../../transportation/service';
import '../../../../transportation/styles.scss';

export const EditPickupScene = (props: IBaseSceneProps) => {

    const store = useStore();
    const dispatch = useDispatch();
    const userServiceCenter = useServiceCenter();
    const [showInfoFlyout, setShowInfoFlyout] = React.useState(false);
    const [service] = React.useState(new TransportationSvc());
    const [pickup, setPickup] = React.useState({} as PickupRead);
    const [editPickup, setEditPickup] = React.useState({} as PickupRead);

    useRouteMiss(
        Routes.SHIPPING_PICKUPS,
        transportActions.clearPickupSearch()
    );

    /**
     * Fetch the pickup and open dealer orders for the page
     * @param pickupId The pickup from the QS
     */
    async function getPickup(pickupId: string) {

        if (pickupId) {
            const result = await service.getPickup(Number.parseInt(pickupId, 10));
            if (result.status === StatusCode.Success) {
                setPickup(result.data);
                return;
            }
        }

        // Create won't have an ID and we'll want to set the
        // service for the truck to be the user's service center
        const t = new PickupRead();
        t.pickupTime = new LocalTimeRead();

        if (userServiceCenter.name !== 'ALL') {
            t.serviceCenterId = userServiceCenter.name;
        }

        setPickup(t);
        onFlyoutShow(t);
    }

    /**
     * Respond to changes in the location
     */
    React.useEffect(() => {
        const opts = props.location.pathname.split(Routes.SHIPPING_PICKUP_EDIT + '/');
        getPickup(opts[1]);
    }, [props.location]);

    /**
     * Download the load sheet for this pickup
     */
    const downloadLoadsheet = async () => {
        const req = new LoadSheetDocument(pickup.dealerId, pickup.id.toString());
        dispatch(getDocumentHelper(req));
    };

    /**
     * Mark the current pickup as delivered
     */
    const onMarkPickupDelivered = async () => {

        const result = await service.markPickupAsDelivered(pickup.id, pickup.rowVersion);
        if (result.status === StatusCode.Success) {
            const tmp = { ...pickup };
            tmp.status = PickupStatus.Delivered;
            setPickup(tmp);
        }
    };

    /**
     * Handler for deleting the current shipment
     */
    const onDelete = async () => {
        // We need an ID and it must be open to try a delete
        if (pickup.id && pickup.status === PickupStatus.Open) {

            const result = await ResultDialog.show({
                title: 'Delete?',
                message: 'Are you sure you want to delete this pickup?'
            });

            if (result) {
                await service.deletePickup(pickup);
            }
        }
    };

    const onFlyoutShow = (p: PickupRead) => {
        const cloned = (p && p.serviceCenterId) ? cloneDeep(p) : cloneDeep(pickup);
        setEditPickup(cloned);
        setShowInfoFlyout(true);
    };

    const onFlyoutClose = async () => {
        setShowInfoFlyout(false);
    };

    const onFlyoutSuccess = async (result: PickupRead) => {
        if (result && pickup.id) {
            setPickup(result);
            setEditPickup({} as unknown as PickupRead);
            setShowInfoFlyout(false);
        }
        else if (result) {
            setEditPickup({} as unknown as PickupRead);
            setShowInfoFlyout(false);
            navigateToPage(Routes.SHIPPING_PICKUP_EDIT_WITH_ID.formattify(result.id));
        }
    };

    const hasCodOrders = () => {

        let retValue = false;

        if (pickup && pickup.orders) {
            for (let i = 0; i < pickup.orders.length; i++) {
                if (pickup.orders[i].paymentTermId.toLowerCase() === 'cod') {
                    retValue = true;
                    break;
                }
            }
        }

        return retValue;
    };

    return !AppUtils.IsEmpty(pickup)
        ? <Pages.PageContent pageName={PageNames.TRANSPORTATION_PICKUP_DETAIL}>
            <Pages.PageHeading
                shouldStick={true}
                showBackBtn={true}
                onBackClick={() => navigateToPage(Routes.SHIPPING_PICKUPS)}
                title={pickup.id > 0
                    ? hasCodOrders()
                        ? <div className="d-inline-block">{pickup.dealerName} <span className="badge secondary purple">COD</span></div>
                        : pickup.dealerName
                    : "Create Pickup"
                }>
                {
                    (pickup.status !== PickupStatus.Delivered)
                        ? <Forms.PageActions
                            id="pickup-edit-form"
                            onEdit={onFlyoutShow}
                            editPermissions={[
                                Permissions.ShippingPickupsEdit
                            ]}
                            onDelete={onDelete}
                            deletePermissions={[
                                Permissions.ShippingPickupsDelete
                            ]}
                        />
                        : null
                }
            </Pages.PageHeading>
            <Pages.PageBody classes="pickup-edit">
                <div className="row">
                    <div className="col-9">

                        <DataGroupHeader title="Basic Info" />

                        <div className="row">
                            <div className="col-6">
                                <Forms.ReadView
                                    label="Dealer"
                                    value={pickup.dealerName.length > 0 ? `${pickup.dealerName} [${pickup.id}]` : ''}
                                />
                            </div>
                            <div className="col-6">
                                <Forms.ReadView
                                    label="Service Center"
                                    value={pickup.serviceCenterId}
                                />
                            </div>
                            <div className="col-6">
                                <Forms.ReadView
                                    label="Pickup Time"
                                    value={pickup.pickupTime ? toLocalDateString(pickup.pickupTime.local, pickup.pickupTime.timeZone) : ''}
                                />
                            </div>
                            <div className="col-6">
                                <Forms.ReadView
                                    label="Amount"
                                    value={formatCurrency(pickup.amount)}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-12">
                                <DataGroupHeader title={`Orders (${pickup.orders.length})`} />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <Table
                                    transparentBackground={false}
                                    rowClass="shipment-order-row"
                                    noResultsMessage="No orders selected for this pickup yet."
                                    header={['Order ID', 'Description', 'Job Name', 'Amount', 'Production']}
                                    headerClasses={['', '', '', '', 'table-cell-center']}>
                                    {
                                        pickup.orders.map((itm: OrderSummary, idx: number) => (
                                            <tr key={`ordersummary-${idx}`}>
                                                <td>{itm.id}</td>
                                                <td>{itm.description}</td>
                                                <td>{itm.jobName}</td>
                                                <td>{formatCurrency(itm.amount)}</td>
                                                <td className="table-cell-center">
                                                    <ProductionStatusDot status={itm.productionStatus} />
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </Table>
                            </div>
                        </div>
                    </div>

                    <EditPickupSidebar
                        pickup={pickup}
                        updatePickupState={onMarkPickupDelivered}
                        downloadPaperwork={downloadLoadsheet} />

                </div>

                <PickupActionEditInfo
                    visible={showInfoFlyout}
                    onSuccess={onFlyoutSuccess}
                    onClose={onFlyoutClose}
                    pickup={editPickup}
                />

            </Pages.PageBody>
        </Pages.PageContent>
        : null;
};
